import { useTranslation } from "react-i18next";
import { useRemovePersonFromWorkspaceMutation } from "@/features/people/graphql/generated";
import { ModalFileName } from "@/shared/modals";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ConfirmModalProps } from "@/shared/widgets/Confirm";
import { useToast } from "@/shared/contexts/toast.context";

interface RemovePersonFromWorkspaceParams {
  personId: number;
  name: string;
}

export const useRemovePersonFromWorkspace = () => {
  const [showConfirm] = useModal(ModalFileName.CONFIRM);
  const { showToast } = useToast();

  const { t } = useTranslation();
  const [removePerson, { data, loading, error }] =
    useRemovePersonFromWorkspaceMutation({
      refetchQueries: ["People"],
    });

  const handleRemovePerson = async (
    params: RemovePersonFromWorkspaceParams
  ) => {
    const response = await removePerson({
      variables: {
        personId: params.personId,
      },
    });

    if (response.data?.removePersonFromWorkspace?.success) {
      showToast({
        type: "success",
        message:
          response.data?.removePersonFromWorkspace?.message ||
          t("common.removed_successfully"),
      });
    }

    if (error) {
      console.error(error);
      return;
    }
  };

  const handleRemovePersonWithConfirm = (
    params: RemovePersonFromWorkspaceParams,
    modalParams?: ConfirmModalProps
  ) => {
    showConfirm({
      title: t("workspace.confirm_remove_person", { name: params.name }),
      description: t("workspace.confirm_remove_person_description", {
        name: params.name,
      }),
      disclaimer: t("common.remove_action_discaimer"),
      confirmLabel: t("common.confirm_remove"),
      cancelLabel: t("common.cancel_remove"),
      confirmButtonVariant: "destructive",
      onConfirm: () => handleRemovePerson(params),
      ...modalParams,
    });
  };

  return {
    loading,
    removePersonWithConfirm: handleRemovePersonWithConfirm,
    removePerson: handleRemovePerson,
  };
};
